/* ============================================================
   Inventering.jsx — räkna lagret, jämför mot appen och logga
   mellanskillnaden som svinn. Här hanteras även lagervarorna.
   ============================================================ */

import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Modal, askConfirm } from '../components/ui.jsx';
import { PageHead } from '../components/admin-shell.jsx';
import { useStore, actions } from '../store/store.js';
import { kr, isUntracked } from '../store/selectors.js';

export function Inventering() {
  const state = useStore();
  const [counts, setCounts] = useState({});   // 'ing:id' / 'prod:id' → string
  const [saved, setSaved] = useState(null);   // senaste svinnresultat
  const [nI, setNI] = useState({ name: '', stock: '', cost: '', perishable: false });
  const [editIng, setEditIng] = useState(null); // { id, name, stock, cost } — lagervara som redigeras
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);     // tidigare inventering som öppnats

  /* Everything that is counted: varor without a recipe, grupp för grupp i
     bokstavsordning, och sist alla lagervaror som en egen sektion. Grupper
     utan lagersaldo (Hembakat) har inget antal att räkna. Samma ordning som
     man går igenom hyllorna, så räkningen inte hoppar fram och tillbaka. */
  const byName = (a, b) => a.name.localeCompare(b.name, 'sv');
  const sections = [
    ...[...state.groups].sort(byName).map(g => ({
      id: g.id, title: g.name, emoji: g.emoji, color: g.color,
      rows: state.products
        .filter(p => p.groupId === g.id && !p.recipe && !isUntracked(state, p))
        .sort(byName)
        .map(p => ({ kind: 'prod', id: p.id, name: p.name, emoji: p.emoji, expected: p.stock, cost: p.cost })),
    })),
    {
      id: 'lagervaror', title: 'Lagervaror', emoji: '📦', color: 'var(--orange)',
      rows: [...state.ingredients].sort(byName)
        .map(i => ({ kind: 'ing', id: i.id, name: i.name, emoji: i.emoji, expected: i.stock, cost: i.cost })),
    },
  ].filter(s => s.rows.length > 0);
  const rows = sections.flatMap(s => s.rows);
  const key = r => r.kind + ':' + r.id;
  const counted = r => {
    const v = counts[key(r)];
    return v == null || v === '' ? null : Math.max(0, Math.round(+v || 0));
  };
  const touched = rows.filter(r => counted(r) != null);
  const svinnSt = touched.reduce((a, r) => a + Math.max(0, r.expected - counted(r)), 0);
  const svinnKr = touched.reduce((a, r) => a + Math.max(0, r.expected - counted(r)) * r.cost, 0);

  async function save() {
    const lines = touched.map(r => ({ kind: r.kind, id: r.id, name: r.name, expected: r.expected, counted: counted(r) }));
    if (!lines.length || busy) return;
    setBusy(true);
    try {
      await actions.saveInventory(lines);
      setSaved({ svinn: svinnSt, value: svinnKr, n: lines.length });
      setCounts({});
      setTimeout(() => setSaved(null), 4000);
    } catch {
      /* reported by the store — the counts stay so nothing is retyped */
    } finally {
      setBusy(false);
    }
  }

  async function addIng() {
    if (!nI.name.trim()) return;
    try {
      await actions.addIngredient({
        name: nI.name.trim(), stock: +nI.stock || 0, cost: +nI.cost || 0, perishable: !!nI.perishable,
      });
      setNI({ name: '', stock: '', cost: '', perishable: false });
    } catch { /* reported by the store */ }
  }

  async function saveIng() {
    if (!editIng || !editIng.name.trim()) return;
    try {
      await actions.updateIngredient(editIng.id, {
        name: editIng.name.trim(), stock: Math.max(0, +editIng.stock || 0), cost: Math.max(0, +editIng.cost || 0),
      });
      setEditIng(null);
    } catch { /* reported by the store — behåll redigeringen */ }
  }

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Inventering" sub="Räkna lagret och jämför mot vad appen förväntar sig — mellanskillnaden är svinn."
        right={<button className="btn btn-primary" disabled={!touched.length || busy} onClick={save}>
          <Icon name="check" size={19} /> Spara inventering ({touched.length})
        </button>} />
      <div className="admin-body">
        {saved && (
          <div className="card-flat" style={{ marginBottom: 16, padding: '14px 18px', display: 'flex', gap: 11, alignItems: 'center', borderLeft: '5px solid ' + (saved.svinn > 0 ? 'var(--orange)' : 'var(--green)') }}>
            <Icon name={saved.svinn > 0 ? 'warn' : 'check'} size={20} color={saved.svinn > 0 ? 'var(--orange-deep)' : 'var(--green)'} />
            <div style={{ fontSize: 14.5, fontWeight: 700 }}>
              {saved.svinn > 0
                ? <>Inventering sparad — svinn: <b>{saved.svinn} st</b> ({kr(saved.value)} i inköpsvärde) på {saved.n} rader. Lagret är justerat.</>
                : <>Inventering sparad — inget svinn på {saved.n} räknade rader. Snyggt! 👏</>}
            </div>
          </div>
        )}

        <div className="grid-count" style={{ alignItems: 'start' }}>
          {/* count table */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="count-grid count-head count-pad" style={{ gap: 10, padding: '14px 22px 10px', fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1.5px solid var(--line-soft)' }}>
              <span>Vara</span><span>Enligt appen</span><span>Räknat</span><span>Diff</span>
            </div>
            {rows.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontWeight: 600, fontSize: 14 }}>Inget att räkna än — lägg upp varor och lagervaror först.</div>
            )}
            {sections.map(sec => (
              <div key={sec.id}>
                <div className="count-pad" style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 22px 8px', background: '#f4ecdc', borderBottom: '1.5px solid var(--line-soft)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: sec.color, flexShrink: 0 }} />
                  <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 15.5, color: 'var(--green-deep)' }}>{sec.emoji} {sec.title}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>{sec.rows.length} rader</span>
                </div>
                {sec.rows.map(r => {
              const c = counted(r);
              const diff = c == null ? null : c - r.expected;
              return (
                <div key={key(r)} className="count-grid count-row count-pad" style={{ gap: 10, padding: '10px 22px', alignItems: 'center', borderBottom: '1px solid var(--line-soft)', background: r.kind === 'ing' ? '#fbf8f0' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 20 }}>{r.emoji}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                      {r.kind === 'ing' && <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--orange-deep)', letterSpacing: '.4px' }}>LAGERVARA</div>}
                    </div>
                  </div>
                  <span style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16 }}>{r.expected}</span>
                  <input className="input" type="number" inputMode="numeric" placeholder="–" style={{ padding: '9px 12px', width: 90 }}
                    value={counts[key(r)] ?? ''} onChange={e => setCounts(s => ({ ...s, [key(r)]: e.target.value }))} />
                  <span>
                    {diff == null ? <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>—</span>
                      : diff === 0 ? <span className="pill tag-good" style={{ fontSize: 12 }}>Stämmer ✓</span>
                        : diff < 0 ? <span className="pill tag-warn" style={{ fontSize: 12 }}>{diff} st svinn</span>
                          : <span className="pill tag-neutral" style={{ fontSize: 12 }}>+{diff} st över</span>}
                  </span>
                </div>
              );
                })}
              </div>
            ))}
            {touched.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 18, padding: '13px 22px', background: '#fbf7ee', fontWeight: 800, fontSize: 14.5 }}>
                <span>Svinn: <b style={{ color: svinnSt > 0 ? 'var(--orange-deep)' : 'var(--green)' }}>{svinnSt} st</b></span>
                <span>Värde: <b style={{ color: svinnSt > 0 ? 'var(--orange-deep)' : 'var(--green)' }}>{kr(svinnKr)}</b></span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* manage lagervaror */}
            <div className="card" style={{ padding: '20px 22px' }}>
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, marginBottom: 6 }}>Lagervaror</div>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5, fontWeight: 600 }}>
                Allt ni köper in men <b>inte</b> slår in i kassan: bröd, korvar och burgare, men också servetter,
                muggar och ketchup. De räknas här och ingår i lagervärdet.
              </p>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, fontWeight: 600 }}>
                Kopplar du en lagervara till en meny­vara via <i>recept</i> dras den automatiskt vid försäljning –
                annars minskar den bara när ni inventerar eller registrerar svinn.
              </p>
              {state.ingredients.map(i => (
                editIng && editIng.id === i.id ? (
                  /* Redigeringsläge: namn, antal och inköp går att ändra i efterhand. */
                  <div key={i.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--line-soft)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input className="input" style={{ padding: '10px 12px', gridColumn: '1 / -1' }} autoFocus
                        value={editIng.name} onChange={e => setEditIng(s => ({ ...s, name: e.target.value }))} placeholder="Namn" />
                      <div>
                        <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)' }}>Antal i lager</label>
                        <input className="input" style={{ padding: '10px 12px' }} type="number" inputMode="numeric"
                          value={editIng.stock} onChange={e => setEditIng(s => ({ ...s, stock: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)' }}>Inköp (kr)</label>
                        <input className="input" style={{ padding: '10px 12px' }} type="number" inputMode="numeric"
                          value={editIng.cost} onChange={e => setEditIng(s => ({ ...s, cost: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => setEditIng(null)}>Avbryt</button>
                      <button className="btn btn-sm btn-primary" disabled={!editIng.name.trim()} onClick={saveIng}><Icon name="check" size={14} /> Spara</button>
                    </div>
                  </div>
                ) : (
                <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 0', borderBottom: '1px solid var(--line-soft)' }}>
                  <span style={{ fontSize: 18 }}>{i.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {i.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600 }}>
                      {i.stock} st{i.unit ? ` · ${i.unit}` : ''} · inköp {i.cost} kr
                    </div>
                  </div>
                  {/* Marks what actually ends up in the bin, so the svinn dialog
                      can lead with korv and burgare instead of the whole lager. */}
                  <label title="Tillagas i förväg och slängs om den blir över – visas överst i svinnlistan"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', color: i.perishable ? 'var(--orange-deep)' : 'var(--muted)' }}>
                    <input type="checkbox" checked={!!i.perishable} style={{ width: 17, height: 17, accentColor: 'var(--orange)' }}
                      onChange={e => actions.updateIngredient(i.id, { perishable: e.target.checked })} />
                    Slängs
                  </label>
                  <button className="btn btn-sm btn-soft" style={{ padding: '6px 9px' }} onClick={() => actions.restockIngredient(i.id, 10)}><Icon name="plus" size={13} />10</button>
                  <button className="btn btn-icon btn-ghost" style={{ width: 36, height: 36 }} title="Ändra namn, antal eller inköpspris"
                    onClick={() => setEditIng({ id: i.id, name: i.name, stock: String(i.stock), cost: String(i.cost) })}><Icon name="edit" size={15} /></button>
                  <button className="btn btn-icon btn-ghost" style={{ width: 36, height: 36 }} onClick={() => {
                    askConfirm(`„${i.name}” tas bort — menyvaror som använder den visas som SLUT.`,
                      { title: 'Ta bort lagervara?', danger: true, confirmLabel: 'Ta bort' })
                      .then(ok => ok && actions.deleteIngredient(i.id));
                  }}><Icon name="trash" size={15} /></button>
                </div>
                )
              ))}
              {/* Name on its own row — three fields side by side clipped the
                  placeholders once the column narrowed on an iPad. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                <input className="input" style={{ padding: '10px 12px', gridColumn: '1 / -1' }} placeholder="Ny lagervara" value={nI.name} onChange={e => setNI(s => ({ ...s, name: e.target.value }))} />
                <input className="input" style={{ padding: '10px 12px' }} type="number" inputMode="numeric" placeholder="Antal" value={nI.stock} onChange={e => setNI(s => ({ ...s, stock: e.target.value }))} />
                <input className="input" style={{ padding: '10px 12px' }} type="number" inputMode="numeric" placeholder="Inköp kr" value={nI.cost} onChange={e => setNI(s => ({ ...s, cost: e.target.value }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 10, fontSize: 13, fontWeight: 700, color: 'var(--ink-soft)', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!nI.perishable} style={{ width: 17, height: 17, accentColor: 'var(--orange)' }}
                  onChange={e => setNI(s => ({ ...s, perishable: e.target.checked }))} />
                Tillagas i förväg och slängs om den blir över
              </label>
              <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 10 }} disabled={!nI.name.trim()} onClick={addIng}>
                <Icon name="plus" size={16} /> Lägg till lagervara
              </button>
            </div>

            {/* history */}
            <div className="card" style={{ padding: '20px 22px' }}>
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, marginBottom: 10 }}>Tidigare inventeringar</div>
              {(state.inventories || []).length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13.5, fontWeight: 600 }}>Inga inventeringar gjorda ännu.</div>}
              {(state.inventories || []).slice(0, 8).map(v => (
                <button key={v.id} type="button" onClick={() => setView(v)} title="Visa vad som räknades och skillnaderna"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--line-soft)', background: 'transparent', cursor: 'pointer', borderRadius: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      {new Date(v.ts).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} · {new Date(v.ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{v.lines.length} rader räknade</div>
                  </div>
                  {v.svinn > 0
                    ? <span className="pill tag-warn" style={{ fontSize: 12 }}>{v.svinn} st · {kr(v.value)}</span>
                    : <span className="pill tag-good" style={{ fontSize: 12 }}>Inget svinn</span>}
                  <Icon name="arrow" size={16} color="var(--muted)" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {view && <InventoryDetails inv={view} onClose={() => setView(null)} />}
    </div>
  );
}

/* En sparad inventering: vad som räknades och hur det skilde sig från appen.
   Avvikelserna ligger först, det som stämde hopfällt under. */
function InventoryDetails({ inv, onClose }) {
  const lines = (inv.lines || []).map(l => ({ ...l, diff: l.counted - l.expected }));
  const off = lines.filter(l => l.diff !== 0).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff) || a.name.localeCompare(b.name, 'sv'));
  const ok = lines.filter(l => l.diff === 0).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  const [showOk, setShowOk] = useState(off.length === 0);
  const when = new Date(inv.ts);
  const title = when.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });

  const Row = ({ l }) => (
    <div className="count-grid count-row" style={{ gap: 10, padding: '9px 0', alignItems: 'center', borderBottom: '1px solid var(--line-soft)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</div>
        {l.kind === 'ing' && <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--orange-deep)', letterSpacing: '.4px' }}>LAGERVARA</div>}
      </div>
      <span style={{ fontWeight: 700, color: 'var(--muted)' }}>{l.expected}</span>
      <span style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16 }}>{l.counted}</span>
      <span>
        {l.diff === 0 ? <span className="pill tag-good" style={{ fontSize: 12 }}>Stämde ✓</span>
          : l.diff < 0 ? <span className="pill tag-warn" style={{ fontSize: 12 }}>{l.diff} st svinn</span>
            : <span className="pill tag-neutral" style={{ fontSize: 12 }}>+{l.diff} st över</span>}
      </span>
    </div>
  );

  return (
    <Modal title={`Inventering ${title.charAt(0).toUpperCase() + title.slice(1)}`} onClose={onClose} wide
      footer={<><div style={{ flex: 1 }} /><button className="btn btn-primary" onClick={onClose}>Stäng</button></>}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="card-flat" style={{ padding: '10px 16px', flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Räknat</div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22 }}>{lines.length} rader · {when.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        <div className="card-flat" style={{ padding: '10px 16px', flex: 1, minWidth: 140, borderLeft: '5px solid ' + (inv.svinn > 0 ? 'var(--orange)' : 'var(--green)') }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Svinn</div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22, color: inv.svinn > 0 ? 'var(--orange-deep)' : 'var(--green)' }}>
            {inv.svinn > 0 ? `${inv.svinn} st · ${kr(inv.value)}` : 'Inget svinn 👏'}
          </div>
        </div>
      </div>

      <div className="count-grid count-head" style={{ gap: 10, fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', padding: '0 0 8px', borderBottom: '1.5px solid var(--line-soft)' }}>
        <span>Vara</span><span>Enligt appen</span><span>Räknat</span><span>Skillnad</span>
      </div>
      {off.length === 0 && <div style={{ padding: '14px 0', color: 'var(--muted)', fontWeight: 600, fontSize: 14 }}>Alla räknade rader stämde med appen.</div>}
      {off.map(l => <Row key={l.kind + l.id} l={l} />)}

      {ok.length > 0 && off.length > 0 && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setShowOk(s => !s)}>
          {showOk ? 'Dölj' : 'Visa'} {ok.length} rader som stämde
        </button>
      )}
      {showOk && ok.map(l => <Row key={l.kind + l.id} l={l} />)}
    </Modal>
  );
}
